import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useTabNav } from "@/context/TabNavContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ReportCard from "@/components/ReportCard";
import { DeptPicker } from "@/components/DeptPicker";
import TrendsView from "@/pages/TrendsView";
import TeamPanel from "@/pages/TeamPanel";
import MemberView from "@/pages/MemberView";
import TeamPlanView from "@/pages/TeamPlanView";
import MomView from "@/pages/MomView";
import MyTodosView from "@/pages/MyTodosView";
import AttendanceView from "@/pages/AttendanceView";
import LeaveView from "@/pages/LeaveView";
import TicketsView from "@/pages/TicketsView";
import Leaderboard from "@/components/Leaderboard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Loader2,
  Users,
  FileCheck2,
  CalendarRange,
  CalendarCheck,
  Download,
  Filter,
  BarChart3,
  ListChecks,
  ClipboardList,
  Plane,
  Search,
  Wrench,
} from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

export default function ManagerView({ canManageTeam = false, isOwner = false }) {
  const { requestedTab, clearTab } = useTabNav();
  const { user } = useAuth();
  const canAttendance = isOwner || user?.is_dheepak;
  const [tab, setTab] = useState("consolidated");
  useEffect(() => { if (requestedTab) { setTab(requestedTab); clearTab(); } }, [requestedTab, clearTab]);
  const [mode, setMode] = useState("range"); // single | range
  const [singleDate, setSingleDate] = useState(today());
  const [start, setStart] = useState(daysAgo(6));
  const [end, setEnd] = useState(today());
  const [memberId, setMemberId] = useState("all");
  const [managerFilterId, setManagerFilterId] = useState("all");
  const [nameSearch, setNameSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState([]);
  const [members, setMembers] = useState([]);
  const [managerList, setManagerList] = useState([]);
  const [reports, setReports] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/users").then((res) => {
      setMembers(res.data.filter((u) => u.role === "user"));
      setManagerList(res.data.filter((u) => u.role === "manager"));
    }).catch(() => {});
  }, []);

  const memberOptions = managerFilterId === "all"
    ? members
    : members.filter((m) => (m.manager_ids || []).includes(managerFilterId));

  const [overdueCount, setOverdueCount] = useState(0);
  useEffect(() => {
    api.get("/reports/team-plans").then((res) => {
      const now = Date.now();
      let c = 0;
      (res.data.groups || []).forEach((g) => (g.items || []).forEach((it) => {
        if (Math.floor((now - new Date(`${it.date}T00:00:00`).getTime()) / 86400000) >= 7) c++;
      }));
      setOverdueCount(c);
    }).catch(() => {});
  }, []);

  const buildParams = useCallback(() => {
    const params = {};
    if (mode === "single") params.date = singleDate;
    else {
      params.start = start;
      params.end = end;
    }
    if (managerFilterId !== "all") params.manager_id = managerFilterId;
    if (memberId !== "all") params.user_id = memberId;
    if (deptFilter.length) params.departments = deptFilter.join(",");
    return params;
  }, [mode, singleDate, start, end, memberId, managerFilterId, deptFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams();
      const [rep, sum] = await Promise.all([
        api.get("/reports", { params }),
        api.get("/reports/summary", {
          params: mode === "single" ? { date: singleDate } : { start, end },
        }),
      ]);
      setReports(rep.data);
      setSummary(sum.data);
    } catch (e) {
      toast.error("Could not load reports");
    } finally {
      setLoading(false);
    }
  }, [buildParams, mode, singleDate, start, end]);

  useEffect(() => {
    load();
  }, [load]);

  const applyPreset = (preset) => {
    const now = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (preset === "today") {
      setMode("single");
      setSingleDate(today());
    } else if (preset === "yesterday") {
      setMode("single");
      setSingleDate(daysAgo(1));
    } else if (preset === "week") {
      setMode("range");
      setStart(daysAgo(6));
      setEnd(today());
    } else if (preset === "thisweek") {
      const dow = (now.getDay() + 6) % 7; // Mon=0
      const mon = new Date(now); mon.setDate(now.getDate() - dow);
      const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
      setMode("range");
      setStart(iso(mon));
      setEnd(iso(sun));
    } else if (preset === "thismonth") {
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setMode("range");
      setStart(iso(first));
      setEnd(iso(last));
    }
  };

  const exportCsv = () => {
    if (reports.length === 0) {
      toast.error("Nothing to export");
      return;
    }
    const headers = ["Date", "Member", "Email", "Summary", "Tasks Done", "Plan Tomorrow"];
    const rows = reports.map((r) => [
      r.date,
      r.user_name,
      r.user_email,
      r.summary,
      (r.tasks_done || []).join(" | "),
      (r.plan_tomorrow || []).map((p) => (typeof p === "string" ? p : p.text)).join(" | "),
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `team-reports-${mode === "single" ? singleDate : `${start}_${end}`}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  const toggleRead = async (report) => {
    const next = !report.is_read;
    setReports((prev) => prev.map((r) => (r.id === report.id ? { ...r, is_read: next } : r)));
    try {
      await api.patch(`/reports/${report.id}/read`, { read: next });
    } catch (e) {
      setReports((prev) => prev.map((r) => (r.id === report.id ? { ...r, is_read: !next } : r)));
      toast.error("Could not update read status");
    }
  };

  const appreciate = async (report) => {
    const mine = !report.appreciated_by_me;
    setReports((prev) => prev.map((r) => (r.id === report.id ? { ...r, appreciated_by_me: mine } : r)));
    try {
      const { data } = await api.post(`/reports/${report.id}/appreciate`, {});
      await load();
      if (data.appreciated) toast.success("Appreciation sent 🌟");
    } catch (e) {
      setReports((prev) => prev.map((r) => (r.id === report.id ? { ...r, appreciated_by_me: !mine } : r)));
      toast.error("Could not update appreciation");
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
          Manager console
        </h1>
        <p className="text-muted-foreground mt-1">
          Consolidated team reports and trends, filtered by date.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="mb-6 max-w-full overflow-x-auto justify-start" data-testid="manager-tabs">
          <TabsTrigger value="consolidated" data-testid="tab-consolidated">
            <FileCheck2 className="h-4 w-4 mr-1.5" /> Consolidated
          </TabsTrigger>
          <TabsTrigger value="trends" data-testid="tab-trends">
            <BarChart3 className="h-4 w-4 mr-1.5" /> Trends
          </TabsTrigger>
          <TabsTrigger value="teamplans" data-testid="tab-teamplans">
            <ListChecks className="h-4 w-4 mr-1.5" /> Team plans
            {overdueCount > 0 && (
              <span className="ml-1.5 text-[10px] font-semibold bg-destructive/15 text-destructive rounded-full px-1.5 py-0.5" data-testid="overdue-chip">
                {overdueCount} · 7d+
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="mom" data-testid="tab-mom">
            <ClipboardList className="h-4 w-4 mr-1.5" /> MOM
          </TabsTrigger>
          <TabsTrigger value="tickets" data-testid="tab-tickets">
            <Wrench className="h-4 w-4 mr-1.5" /> Facility & IT
          </TabsTrigger>
          <TabsTrigger value="mytodos" data-testid="tab-mytodos">
            <ClipboardList className="h-4 w-4 mr-1.5" /> To Do
          </TabsTrigger>
          <TabsTrigger value="leave" data-testid="tab-leave">
            <Plane className="h-4 w-4 mr-1.5" /> Leave / Off
          </TabsTrigger>
          <TabsTrigger value="leaderboard" data-testid="tab-leaderboard">
            <BarChart3 className="h-4 w-4 mr-1.5" /> Leaderboard
          </TabsTrigger>
          {canAttendance && (
            <TabsTrigger value="attendance" data-testid="tab-attendance">
              <CalendarCheck className="h-4 w-4 mr-1.5" /> Attendance
            </TabsTrigger>
          )}
          {!isOwner && (
            <TabsTrigger value="myreport" data-testid="tab-myreport">
              <FileCheck2 className="h-4 w-4 mr-1.5" /> My report
            </TabsTrigger>
          )}
          {isOwner && (
            <TabsTrigger value="team" data-testid="tab-team">
              <Users className="h-4 w-4 mr-1.5" /> Team
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="teamplans">
          <TeamPlanView />
        </TabsContent>

        <TabsContent value="tickets">
          <TicketsView />
        </TabsContent>

        <TabsContent value="mom">
          <MomView />
        </TabsContent>

        <TabsContent value="mytodos">
          <MyTodosView />
        </TabsContent>

        <TabsContent value="leave">
          <LeaveView />
        </TabsContent>

        <TabsContent value="leaderboard">
          <Leaderboard />
        </TabsContent>

        {canAttendance && (
          <TabsContent value="attendance">
            <AttendanceView />
          </TabsContent>
        )}

        {!isOwner && (
          <TabsContent value="myreport">
            <MemberView />
          </TabsContent>
        )}

        {isOwner && (
          <TabsContent value="team">
            <TeamPanel />
          </TabsContent>
        )}

        <TabsContent value="trends">
          <TrendsView />
        </TabsContent>

        <TabsContent value="consolidated">
      <div className="flex justify-end mb-4">
        <Button
          variant="outline"
          className="rounded-lg"
          onClick={exportCsv}
          data-testid="export-csv-btn"
        >
          <Download className="h-4 w-4 mr-1.5" /> Export CSV
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard
          icon={Users}
          label="Team members"
          value={summary?.total_members ?? "—"}
          tone="sky"
          testid="stat-members"
        />
        <StatCard
          icon={FileCheck2}
          label="Reported"
          value={summary ? `${summary.reported_count}/${summary.total_members}` : "—"}
          tone="emerald"
          testid="stat-reported"
        />
        <StatCard
          icon={CalendarRange}
          label="Report entries"
          value={summary?.report_entries ?? "—"}
          tone="violet"
          testid="stat-entries"
        />
      </div>

      {/* Filters */}
      <Card className="p-5 sm:p-6 rounded-xl mb-6">
        <div className="flex items-center gap-1.5 mb-4 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          <Filter className="h-4 w-4" /> Filters
        </div>
        <div className="flex flex-wrap gap-2 mb-4" data-testid="date-presets">
          {[
            ["today", "Today"],
            ["yesterday", "Yesterday"],
            ["week", "Last 7 days"],
            ["thisweek", "This week"],
            ["thismonth", "This month"],
          ].map(([k, label]) => (
            <Button
              key={k}
              variant="secondary"
              size="sm"
              className="rounded-full"
              data-testid={`preset-${k}`}
              onClick={() => applyPreset(k)}
            >
              {label}
            </Button>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
          <div className="space-y-2">
            <Label>Filter mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="filter-mode-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="single" data-testid="mode-single">Single date</SelectItem>
                <SelectItem value="range" data-testid="mode-range">Date range</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {mode === "single" ? (
            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={singleDate}
                max={today()}
                onChange={(e) => setSingleDate(e.target.value)}
                data-testid="single-date-input"
              />
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label>From</Label>
                <Input
                  type="date"
                  value={start}
                  max={end}
                  onChange={(e) => setStart(e.target.value)}
                  data-testid="range-start-input"
                />
              </div>
              <div className="space-y-2">
                <Label>To</Label>
                <Input
                  type="date"
                  value={end}
                  max={today()}
                  onChange={(e) => setEnd(e.target.value)}
                  data-testid="range-end-input"
                />
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label>Manager</Label>
            <Select value={managerFilterId} onValueChange={(v) => { setManagerFilterId(v); setMemberId("all"); }}>
              <SelectTrigger data-testid="manager-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All managers</SelectItem>
                {managerList.map((m) => (
                  <SelectItem key={m.id} value={m.id} data-testid={`manager-option-${m.id}`}>
                    {m.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Team member</Label>
            <Select value={memberId} onValueChange={setMemberId}>
              <SelectTrigger data-testid="member-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All members</SelectItem>
                {memberOptions.map((m) => (
                  <SelectItem key={m.id} value={m.id} data-testid={`member-option-${m.id}`}>
                    {m.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Search member</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={nameSearch} onChange={(e) => setNameSearch(e.target.value)} placeholder="Type a name…" className="pl-8" data-testid="report-name-search" />
            </div>
          </div>
        </div>
        <div className="mt-4 space-y-2">
          <Label className="flex items-center gap-2">Departments {deptFilter.length > 0 && <button type="button" className="text-[11px] text-primary font-normal" onClick={() => setDeptFilter([])} data-testid="dept-filter-clear">Clear</button>}</Label>
          <DeptPicker value={deptFilter} onChange={setDeptFilter} testid="consolidated-dept-filter" />
        </div>
      </Card>

      {/* Reports */}
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : reports.length === 0 ? (
        <Card className="p-12 text-center rounded-xl" data-testid="manager-empty">
          <FileCheck2 className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-muted-foreground">No reports for this selection.</p>
        </Card>
      ) : (
        <div className="space-y-6" data-testid="manager-reports-list">
          {(() => {
            const q = nameSearch.trim().toLowerCase();
            const visible = q ? reports.filter((r) => (r.user_name || "").toLowerCase().includes(q) || (r.summary || "").toLowerCase().includes(q)) : reports;
            const unread = visible.filter((r) => !r.is_read);
            const read = visible.filter((r) => r.is_read);
            return (
              <>
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground">Unread</h3>
                    <span className="text-xs bg-primary/10 text-primary rounded-full px-2 py-0.5" data-testid="unread-count">{unread.length}</span>
                  </div>
                  {unread.length === 0 ? (
                    <Card className="p-6 text-center rounded-xl" data-testid="all-read"><p className="text-sm text-muted-foreground">All caught up — no unread reports in this selection.</p></Card>
                  ) : (
                    <div className="space-y-4" data-testid="unread-list">
                      {unread.map((r, i) => (
                        <ReportCard key={r.id} report={r} showUser index={i} onToggleRead={toggleRead} onAppreciate={appreciate} />
                      ))}
                    </div>
                  )}
                </div>
                {read.length > 0 && (
                  <details className="group" data-testid="read-section">
                    <summary className="cursor-pointer list-none flex items-center gap-2 mb-3 select-none">
                      <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground">Read</h3>
                      <span className="text-xs bg-secondary rounded-full px-2 py-0.5 text-muted-foreground">{read.length}</span>
                      <span className="text-xs text-muted-foreground group-open:hidden">· click to expand</span>
                    </summary>
                    <div className="space-y-4 mt-2">
                      {read.map((r, i) => (
                        <ReportCard key={r.id} report={r} showUser index={i} onToggleRead={toggleRead} onAppreciate={appreciate} />
                      ))}
                    </div>
                  </details>
                )}
              </>
            );
          })()}
        </div>
      )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone, testid }) {
  const toneMap = {
    sky: "text-foreground bg-secondary",
    emerald: "text-primary bg-primary/10",
    violet: "text-foreground bg-secondary",
    rose: "text-destructive bg-destructive/10",
  };
  return (
    <Card className="p-4 sm:p-5 rounded-xl" data-testid={testid}>
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center mb-3 ${toneMap[tone]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{value}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
    </Card>
  );
}
